import { useAutocomplete } from '@/hooks/SearchBar/useAutocomplete';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useStyle } from '@/classify';
import { useRouter } from 'next/navigation';
import defaultClasses from './autocomplete.module.css';
import Suggestions from './suggestions';

const GET_AUTOCOMPLETE_RESULTS = gql`
  query getAutocompleteResults($inputText: String!) {
    # Limit results to first three.
    products(search: $inputText, currentPage: 1, pageSize: 5) {
      aggregations {
        label
        count
        attribute_code
        options {
          label
          value
        }
        position
      }
      # eslint-disable-next-line @graphql-eslint/require-id-when-available
      items {
        id
        uid
        sku
        name
        small_image {
          url
        }
        url_key
        url_suffix
        price {
          regularPrice {
            amount {
              value
              currency
            }
          }
        }
        price_range {
          maximum_price {
            final_price {
              currency
              value
            }
            discount {
              amount_off
            }
          }
        }
      }
      page_info {
        total_pages
      }
      total_count
    }
  }
`;

const Autocomplete = props => {
  const { setVisible, valid, visible, afterSubmit, searchValue } = props;
  const talonProps = useAutocomplete({
    queries: {
      getAutocompleteResults: GET_AUTOCOMPLETE_RESULTS,
    },
    valid,
    visible,
  });

  const router = useRouter();

  const { displayResult, filters, messageType, products, resultCount, value } = talonProps;

  const classes = useStyle(defaultClasses, props.classes);
  const rootClassName = visible ? classes.root_visible : classes.root_hidden;

  const { formatMessage } = useIntl();
  const MESSAGES = new Map()
    .set(
      'ERROR',
      formatMessage({
        id: 'autocomplete.error',
        defaultMessage: 'An error occurred while fetching results.',
      }),
    )
    .set(
      'LOADING',
      formatMessage({
        id: 'autocomplete.loading',
        defaultMessage: 'Fetching results...',
      }),
    )
    .set(
      'PROMPT',
      formatMessage({
        id: 'autocomplete.prompt',
        defaultMessage: 'Search for a product',
      }),
    )
    .set(
      'EMPTY_RESULT',
      formatMessage({
        id: 'autocomplete.emptyResult',
        defaultMessage: 'Sorry, no products were found for your search',
      }),
    )
    .set('RESULT_SUMMARY', (_, resultCount) =>
      formatMessage(
        {
          id: 'autocomplete.resultSummaryResult',
          defaultMessage: 'Showing {resultCount} search results for ' + `'${searchValue}'`,
        },
        {
          resultCount: resultCount,
        },
      ),
    )
    .set(
      'INVALID_CHARACTER_LENGTH',
      formatMessage({
        id: 'autocomplete.invalidCharacterLength',
        defaultMessage: 'Search term must be at least three characters',
      }),
    );

  const messageTpl = MESSAGES.get(messageType);
  const message = typeof messageTpl === 'function' ? messageTpl`${resultCount}` : messageTpl;

  const destination = useMemo(() => {
    if (resultCount > 1) {
      return '/search.html?query=' + value + '&p=1';
    } else if (resultCount === 1) {
      const { items } = products;

      return items.length > 0 ? `/${items[0].url_key}${items[0].url_suffix}` : null;
    }
  }, [products, resultCount, value]);

  const messageEle = useMemo(() => {
    const temp =
      messageType === 'RESULT_SUMMARY' ? (
        <span
          onClick={() => {
            router.push(destination);
            afterSubmit && afterSubmit();
          }}
        >
          {message}
        </span>
      ) : messageType === 'EMPTY_RESULT' ? null : (
        message
      );

    return temp;
  }, [afterSubmit, destination, message, messageType, router]);

  const messageClass = messageType === 'RESULT_SUMMARY' ? classes.messageClickable : classes.message;

  if (!value) {
    return null;
  }

  return (
    <div className={rootClassName}>
      <div className={classes.suggestions}>
        <Suggestions
          displayResult={displayResult}
          products={products || {}}
          filters={filters}
          searchValue={value}
          setVisible={setVisible}
          afterSubmit={afterSubmit}
          visible={visible}
        />
      </div>
      <label
        id='search_query'
        className={messageClass}
      >
        {messageEle}
      </label>
    </div>
  );
};

export default Autocomplete;
